import { Fragment, type ReactNode } from "react";

/**
 * Spec 020 US5 (T036) — shape-parameterized reel-grid renderer.
 *
 * The viewer's only reel-grid surface (the spin replay) historically
 * hardcoded a 5-reel × 3-row layout. Spec 020 parameterizes the grid
 * (`{reels: N, rows: M}` uniform, or `{reels: [3,4,4,4,3]}` per-reel
 * fixed heights), so the viewer must render each declared geometry:
 * a 3×3 reads as a compact fruit machine, a 6×4 as a larger grid, and
 * a pyramid tapers — the short outer reels render WITHOUT phantom dead
 * cells (research.md R3).
 *
 * Layout: a flex row of vertical reel-columns. Each column stacks its
 * own `height[r]` cells; the row is `items-center`, so a short column
 * (a 3-high outer reel against 4-high inner reels) centers vertically
 * against its taller neighbors — the Egyptian-pyramid silhouette. For a
 * uniform grid every column is the same height and `items-center` is a
 * no-op, so a 5×3 render is visually identical to the pre-spec-020
 * fixed grid.
 *
 * Geometry is self-describing: pass a reel-major `grid` (`grid[reel]`
 * is one reel's live cells, `grid[reel].length` = that reel's height)
 * and the component derives `reelCount` / `rowHeights` from it. For a
 * shape-only preview (no symbols yet), pass an explicit `geometry`
 * instead. No `5` or `3` is hardcoded — every dimension comes from the
 * data.
 *
 * Cell rendering is delegated: callers that need per-cell decoration
 * (the replay view's sticky / walking / expanding wild markers) pass a
 * `renderCell` that returns the full cell node. The default renderer
 * draws a plain bordered cell with the symbol, used by standalone
 * config-shape previews.
 */

export interface GridGeometry {
  /** Number of reels (columns). */
  reelCount: number;
  /** Per-reel live-cell heights; `rowHeights.length === reelCount`. */
  rowHeights: number[];
}

/**
 * Derive geometry from a reel-major symbol grid. Each entry is one
 * reel's column of live cells, so the per-reel height is simply that
 * column's length — which is exactly what makes pyramids self-describe.
 */
export function gridGeometryFromGrid(grid: string[][]): GridGeometry {
  return {
    reelCount: grid.length,
    rowHeights: grid.map((column) => column.length),
  };
}

/**
 * Derive geometry from a Rust wire-format `ExecutionPlan` JSON object
 * (spec 020 T006 adds `reel_count` / `row_heights` / `max_row`). Used
 * for a shape-only preview before any spin has been replayed. Returns
 * `null` when the object lacks the geometry fields (a pre-spec-020
 * plan) so callers can fall back to the default 5×3.
 */
export function gridGeometryFromPlan(
  plan: Record<string, unknown> | null | undefined,
): GridGeometry | null {
  if (plan == null) return null;
  const reelCount = plan["reel_count"];
  const rowHeights = plan["row_heights"];
  if (
    typeof reelCount === "number" &&
    Array.isArray(rowHeights) &&
    rowHeights.length === reelCount &&
    rowHeights.every((h) => typeof h === "number")
  ) {
    return { reelCount, rowHeights: rowHeights as number[] };
  }
  return null;
}

export interface GridPreviewProps {
  /**
   * Reel-major symbol grid (`grid[reel][row]`). When present, geometry
   * and cell symbols both derive from it. Takes precedence over
   * `geometry`.
   */
  grid?: string[][];
  /**
   * Explicit geometry for a shape-only preview (cells render blank
   * unless `renderCell` supplies content). Required when `grid` is
   * absent.
   */
  geometry?: GridGeometry;
  /**
   * Per-cell renderer returning the full cell node (wrapper included),
   * so callers own className / data-attributes / styles. When omitted,
   * a plain bordered cell with the symbol is drawn.
   */
  renderCell?: (reel: number, row: number, symbol: string) => ReactNode;
  /** `data-testid` on the outer container. Default `grid-preview`. */
  testId?: string;
  /**
   * `data-testid` factory for the default cell renderer. Ignored when
   * `renderCell` is supplied (those cells carry their own testids).
   */
  cellTestId?: (reel: number, row: number) => string;
}

/**
 * Render a reel grid at its declared geometry. Pass a reel-major `grid`
 * (geometry + symbols self-describe from it) or an explicit `geometry`
 * for a shape-only preview. See the module docstring for the layout
 * rationale (flex reel-columns, `items-center` pyramid taper).
 */
export function GridPreview({
  grid,
  geometry,
  renderCell,
  testId = "grid-preview",
  cellTestId = (reel, row) => `grid-cell-${reel}-${row}`,
}: GridPreviewProps) {
  const geo = grid != null ? gridGeometryFromGrid(grid) : geometry;
  if (geo == null || geo.reelCount === 0) return null;

  const symbolAt = (reel: number, row: number): string =>
    grid?.[reel]?.[row] ?? "";

  const cell = (reel: number, row: number): ReactNode => {
    const symbol = symbolAt(reel, row);
    if (renderCell) return renderCell(reel, row, symbol);
    return (
      <div
        data-testid={cellTestId(reel, row)}
        className="flex h-12 w-full items-center justify-center border border-paper-border text-xs font-bold text-paper-text dark:border-term-border dark:text-term-text"
      >
        <span className="truncate px-1">{symbol}</span>
      </div>
    );
  };

  return (
    <div
      data-testid={testId}
      className="flex flex-row items-center gap-1"
      style={{ alignItems: "center" }}
    >
      {Array.from({ length: geo.reelCount }, (_, reel) => {
        const height = geo.rowHeights[reel] ?? 0;
        return (
          <div
            key={reel}
            data-testid={`${testId}-reel-${reel}`}
            data-reel-height={height}
            className="flex flex-1 flex-col gap-1"
          >
            {Array.from({ length: height }, (_, row) => (
              <Fragment key={row}>{cell(reel, row)}</Fragment>
            ))}
          </div>
        );
      })}
    </div>
  );
}
